import { TuiDocument } from '../core/Document';
import { Layer } from '../core/Layer';
import { Selection } from '../core/Selection';
import { Cell, Color, Point } from '../core/types';
import { LineStyle } from '../unicode/boxdrawing';
import { GradientDef } from './gradient';

export type ToolId =
  | 'select'
  | 'lasso'
  | 'wand'
  | 'move'
  | 'pencil'
  | 'brush'
  | 'eraser'
  | 'text'
  | 'line'
  | 'rect'
  | 'ellipse'
  | 'box'
  | 'arrow'
  | 'fill'
  | 'gradient'
  | 'braille'
  | 'brailleLine'
  | 'brailleRect'
  | 'brailleEllipse'
  | 'widget'
  | 'eyedropper';

export type ToolGroup = 'select' | 'draw' | 'shape' | 'color' | 'insert';

/** A single cell write produced by a tool. */
export interface CellWrite {
  x: number;
  y: number;
  cell: Cell;
}

export type WidgetKind =
  | 'panel'
  | 'progress'
  | 'button'
  | 'input'
  | 'table'
  | 'list'
  | 'scrollbar'
  | 'separator'
  | 'titlebar';

export interface ProgressStyle {
  full: string;
  empty: string;
  capLeft: string;
  capRight: string;
  percent: number;
  showLabel: boolean;
}

export interface WidgetOptions {
  kind: WidgetKind;
  title: string;
  label: string;
  lineStyle: LineStyle;
  progress: ProgressStyle;
  /** Table header labels. */
  columns: string[];
  /** Row contents; table rows split on "|". */
  items: string[];
  /** Fill the interior with the background colour. */
  filled: boolean;
}

export interface BrushState {
  char: string;
  fg: Color;
  bg: Color;
  attrs: number;
  size: number;
  /** Which channels a stroke writes. Lets the brush recolour without
   *  disturbing characters, and vice versa. */
  paintChar: boolean;
  paintFg: boolean;
  paintBg: boolean;
  lineStyle: LineStyle;
  filled: boolean;
  gradient: GradientDef;
  widgetOptions: WidgetOptions;
  /** Braille tools: erase dots instead of setting them. */
  brailleErase: boolean;
  /** Fill tool: match on the composite instead of the active layer. */
  sampleComposite: boolean;
  /** Fill tolerance, 0 = exact match. */
  tolerance: number;
}

/** A pointer interaction in progress, in cell coordinates. */
export interface ToolStroke {
  start: Point;
  current: Point;
  /** Every cell the pointer visited, in order (freehand tools). */
  points: Point[];
  /** Fractional position inside the current cell, 0..1 (Braille tools). */
  startFrac: Point;
  currentFrac: Point;
  /** Sub-cell trail: cell coords plus fraction, in order. */
  fracPoints: Array<{ x: number; y: number; fx: number; fy: number }>;
  shift: boolean;
  alt: boolean;
  ctrl: boolean;
  /** 0 = primary, 2 = secondary (draws with the background colour). */
  button: number;
}

export interface ToolEnv {
  doc: TuiDocument;
  layer: Layer;
  selection: Selection;
  brush: BrushState;
  /** Active-layer read. */
  read(x: number, y: number): Cell;
  /** Flattened read, for sampling and fills that respect other layers. */
  readComposite(x: number, y: number): Cell;
}

export interface SelectionResult {
  selection: Selection;
  mode: 'replace' | 'add' | 'subtract';
}

export interface Tool {
  id: ToolId;
  name: string;
  group: ToolGroup;
  shortcut?: string;
  hint: string;
  /** Sample every pointer position, not just start/current. */
  freehand?: boolean;
  /** Acts once on pointer-down. */
  instant?: boolean;
  /** Cells this stroke would write. Called for both preview and commit. */
  plan?(stroke: ToolStroke, env: ToolEnv): CellWrite[];
  /** Selection tools return a mask instead of writes. */
  planSelection?(stroke: ToolStroke, env: ToolEnv): SelectionResult | null;
  /** Eyedropper-style tools return brush changes. */
  pick?(stroke: ToolStroke, env: ToolEnv): Partial<BrushState> | null;
}

/** Applies the brush's channel flags on top of an existing cell. */
export function paintCell(existing: Cell, brush: BrushState, char?: string, overrides?: Partial<Cell>): Cell {
  const useChar = char !== undefined ? char : brush.char;
  return {
    char: brush.paintChar ? useChar : existing.char,
    fg: overrides?.fg ?? (brush.paintFg ? brush.fg : existing.fg),
    bg: overrides?.bg ?? (brush.paintBg ? brush.bg : existing.bg),
    attrs: overrides?.attrs ?? (brush.paintChar || brush.paintFg ? brush.attrs : existing.attrs),
  };
}

export function cellsEqualEnough(a: Cell, b: Cell, tolerance: number): boolean {
  if (tolerance <= 0) return a.char === b.char && a.fg === b.fg && a.bg === b.bg;
  const close = (x: number, y: number) => {
    if (x === y) return true;
    if (x < 0 || y < 0) return false;
    const dr = ((x >> 16) & 0xff) - ((y >> 16) & 0xff);
    const dg = ((x >> 8) & 0xff) - ((y >> 8) & 0xff);
    const db = (x & 0xff) - (y & 0xff);
    return Math.sqrt(dr * dr + dg * dg + db * db) <= tolerance;
  };
  return a.char === b.char && close(a.fg, b.fg) && close(a.bg, b.bg);
}
